import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export function formatApiError(detail, fallback = "Bir hata oluştu. Tekrar deneyin.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const EUR_RATE = 38;

// Storefront money config — defaults match backend; fetchConfig() overrides from /api/config
// so VAT rates and shipping tiers have a single source of truth on the server.
export const CONFIG = {
  vatRates: {
    DE: 0.19, FR: 0.20, AT: 0.20, BE: 0.21, NL: 0.21, ES: 0.21, IT: 0.22,
    IE: 0.23, PT: 0.23, PL: 0.23, CZ: 0.21, SK: 0.20, HU: 0.27, HR: 0.25,
    DK: 0.25, SE: 0.25, FI: 0.255, GR: 0.24, RO: 0.19, BG: 0.20, CY: 0.19,
    MT: 0.18, LU: 0.17, EE: 0.22, LV: 0.21, LT: 0.21, SI: 0.22,
  },
  shipDE: 4.99,
  shipEU: 9.99,
  shipIntl: 14.99,
  freeShip: 100,
  googleAdsConversion: null,
  site: null,
};

let configPromise = null;

/* cache the IN-FLIGHT promise, not a "started" flag — otherwise a second caller
   (Header + Home mount together) reads CONFIG before the fetch resolves */
export function fetchConfig() {
  if (!configPromise) {
    configPromise = api.get("/config").then(({ data }) => {
      if (data.vat_rates) CONFIG.vatRates = data.vat_rates;
      if (data.shipping) {
        CONFIG.shipDE = data.shipping.DE ?? CONFIG.shipDE;
        CONFIG.shipEU = data.shipping.EU ?? CONFIG.shipEU;
        CONFIG.shipIntl = data.shipping.INTL ?? CONFIG.shipIntl;
      }
      if (data.free_shipping_eur != null) CONFIG.freeShip = data.free_shipping_eur;
      if (data.google_ads_conversion) CONFIG.googleAdsConversion = data.google_ads_conversion;
      if (data.site) {
        CONFIG.site = data.site;
        applyDropTheme(data.site.theme);
      }
    }).catch(() => {
      // keep defaults
    }).then(() => CONFIG);
  }
  return configPromise;
}

export function applyDropTheme(theme) {
  if (!theme || typeof document === "undefined") return;
  const s = document.documentElement.style;
  s.setProperty("--zjz-bg", theme.bg);
  s.setProperty("--zjz-surface", theme.surface);
  s.setProperty("--zjz-border", theme.border);
  s.setProperty("--zjz-text", theme.text);
  s.setProperty("--zjz-muted", theme.muted);
  s.setProperty("--zjz-accent", theme.accent);
  s.setProperty("--zjz-accent-bright", theme.accent_bright);
}

export function vatFor(country) {
  return CONFIG.vatRates[country] ?? 0.19;
}

/* contained VAT portion of a GROSS amount (prices are final, VAT included) */
export function vatContained(grossAmount, country) {
  const rate = vatFor(country);
  return Math.round((grossAmount - grossAmount / (1 + rate)) * 100) / 100;
}

export function shipFor(country, subtotal = 0) {
  if (subtotal >= CONFIG.freeShip) return 0;
  if (country === "DE") return CONFIG.shipDE;
  return CONFIG.shipEU;
}

export function formatAmount(amount, currency = "eur") {
  return new Intl.NumberFormat(currency === "eur" ? "de-DE" : "tr-TR", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: currency === "eur" ? 2 : 0,
  }).format(amount);
}

export function formatPrice(value, currency = "eur") {
  return formatAmount(value, currency);
}

/* Single shared in-flight/cache for the public product list — Home, Header and
   Footer all need it; without this they fired 3 identical requests on load. */
let productsPromise = null;
export function fetchProductsCached() {
  if (!productsPromise) {
    productsPromise = api.get("/products").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []);
  }
  return productsPromise;
}

export default api;
